import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wallet, WifiOff, Lock, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { TPACloseInputs } from "../cash/TPACloseInputs";
import { formatCurrency, toBaseCurrency, getCurrencySymbol } from "../../lib/utils";

const METHODS = [["cash", "Numerário"], ["card", "Multicaixa"], ["transfer", "Transferência"], ["mobile", "Pag. Móvel"]];

// Fecho de caixa a partir do POS (online → servidor; offline → guardado e conciliado ao voltar a ligação)
export const POSCashCloseModal = ({ open, onClose, session, online, onConfirm, busy }) => {
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const [tpaCounts, setTpaCounts] = useState({});
  useEffect(() => { if (open) { setCounted(""); setNotes(""); setTpaCounts({}); } }, [open]);

  const tpas = (session?.tpa_expected || []).filter((t) => t.tpa_id);
  const noTpaRow = (session?.tpa_expected || []).find((t) => !t.tpa_id);
  const countedBase = counted !== "" ? toBaseCurrency(counted) : null;
  const diff = countedBase !== null ? countedBase - (session?.expected_cash || 0) : null;
  const methodTotals = useMemo(() => METHODS.map(([id, label]) => [id, label, session?.[`total_${id}`] || 0]).filter((x) => x[2] > 0), [session]);
  const cardEntries = useMemo(() => (session?.tpa_expected || []).reduce((s, t) => s + (t.entries || 0), 0), [session]);

  const confirm = () => {
    if (countedBase === null || isNaN(countedBase) || countedBase < 0) return toast.error("Indique o valor contado em caixa");
    const missing = tpas.filter((t) => tpaCounts[t.tpa_id] === undefined || tpaCounts[t.tpa_id] === "");
    if (missing.length) return toast.error(`Indique o valor de fecho do TPA: ${missing.map((t) => t.name).join(", ")}`);
    onConfirm({
      closing_balance: countedBase,
      notes: notes.trim() || null,
      tpa_counts: tpas.map((t) => ({ tpa_id: t.tpa_id, amount: toBaseCurrency(tpaCounts[t.tpa_id]) })),
      expected_cash: session?.expected_cash || 0,
      tpa_expected: tpas,
      total_sales: session?.total_sales || 0,
    });
  };

  if (!session) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#1e1e1e] text-zinc-100 border-zinc-700 max-h-[90vh] overflow-y-auto" data-testid="pos-cash-close-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-red-400" /> Fechar caixa{!online || session.offline ? " (OFFLINE)" : ""}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {!online || session.offline
              ? "Sem ligação: o fecho fica guardado neste dispositivo e é conciliado com o servidor assim que a ligação voltar. O esperado abaixo inclui as vendas offline deste terminal."
              : "Conte o dinheiro na gaveta e introduza os totais dos talões de fecho dos TPA. A caixa fica fechada e o POS bloqueado até nova abertura."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3">
              <p className="text-[11px] uppercase text-zinc-400">Esperado em caixa (numerário)</p>
              <p className="font-mono font-black text-xl text-emerald-300" data-testid="pos-cash-close-expected">{formatCurrency(session.expected_cash || 0)}</p>
              <p className="text-[11px] text-zinc-500">fundo {formatCurrency(session.opening_balance || 0)} + vendas em numerário</p>
            </div>
            <div className="rounded-lg border border-zinc-700 p-3">
              <p className="text-[11px] uppercase text-zinc-400">Vendas da sessão</p>
              <p className="font-mono font-bold text-lg" data-testid="pos-cash-close-total-sales">{formatCurrency(session.total_sales || 0)}</p>
              <p className="text-[11px] text-zinc-500">{methodTotals.map(([, label, v]) => `${label} ${formatCurrency(v)}`).join(" · ") || "sem vendas"}{session.offline_sales_count ? ` · ${session.offline_sales_count} venda(s) offline` : ""}{cardEntries ? ` · +${cardEntries} recebimento(s) com cartão` : ""}</p>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-zinc-400">Valor contado em caixa ({getCurrencySymbol()}) *</Label>
            <div className="relative">
              <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input type="number" min="0" step="0.01" value={counted} onChange={(e) => setCounted(e.target.value)} autoFocus className="pl-10 bg-zinc-900 border-zinc-700 font-mono h-11 text-lg" data-testid="pos-cash-close-counted" />
            </div>
            {diff !== null && !isNaN(diff) && (
              <p className={`text-xs font-semibold ${diff < 0 ? "text-red-400" : diff > 0 ? "text-emerald-400" : "text-zinc-400"}`} data-testid="pos-cash-close-diff">
                Diferença: {diff > 0 ? "+" : ""}{formatCurrency(diff)}{diff === 0 ? " · confere" : diff < 0 ? " · falta dinheiro" : " · sobra"}
              </p>
            )}
          </div>
          <TPACloseInputs tpas={[...tpas, ...(noTpaRow ? [noTpaRow] : [])]} values={tpaCounts} onChange={setTpaCounts} />
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observações do fecho (opcional)" className="bg-zinc-900 border-zinc-700" data-testid="pos-cash-close-notes" />
          {(!online || session.offline) && <p className="flex items-start gap-2 text-xs text-amber-300" data-testid="pos-cash-close-offline-hint"><WifiOff className="w-4 h-4 shrink-0" /> Após o fecho offline o POS fica bloqueado até a conciliação terminar (vendas offline → servidor → fecho). Se outros terminais venderam entretanto, o esperado final pode ser diferente.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button className="bg-red-600 hover:bg-red-500 gap-2" onClick={confirm} disabled={busy} data-testid="pos-cash-close-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} {!online || session.offline ? "Guardar fecho offline" : "Fechar caixa"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
